import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { centroidOfGeometry } from '../utils/coords';
import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { addOrUpdateGeoJsonSource, registerCursorHandlers, removeSourceAndLayers } from '../utils/maplibre-helpers';
import { drillDownTransactionCluster } from '../modules/transaction-drilldown';
import { isCoarsePointer, pointHitLayer, pointHitRadius } from '../utils/point-hit-area';
import { groupTransactionMarkers } from '../modules/transaction-screen-clusters';

// The transactions department colour (DESIGN.md §5a: --dept-transactions,
// navy), the same as the „Transakcje” switch and the detail pages. The map
// token --nm-layer-transactions mirrors it for the legend and HTML markers; the
// basemap stays light in both themes, so the map uses the light value only.
export const TRANSACTION_COLOR = '#1e3a8a';
const TRANSACTION_RGB = '30,58,138';

function transactionColor(alpha) {
    return 'rgba(' + TRANSACTION_RGB + ',' + alpha + ')';
}

function getFeatureCenter(feature) {
    if (!feature || !feature.geometry) return null;
    const center = centroidOfGeometry(feature.geometry);
    return center ? { lat: center[1], lng: center[0] } : null;
}

export default class TransactionsLayer extends GeoJsonFetchLayer {
    static ID = 'transakcje';
    static DESCRIPTION = 'Transakcje nieruchomościami';
    static MIN_ZOOM = 0;
    static SOURCE_ID = 'src-transactions';
    static BASE_LAYER_IDS = [
        'lyr-transactions-circle',
        'lyr-transactions-fill',
        'lyr-transactions-line',
        'lyr-transactions-symbol',
        'lyr-transactions-hit'
    ];
    // Invisible enlarged circle under point features: clicks, taps and the
    // hover cursor reach a dot without the dot growing (see point-hit-area.js).
    static HIT_LAYER_ID = 'lyr-transactions-hit';
    // Union of the clickable layers, so the cursor stays a pointer when it
    // moves from a parcel outline onto the hit circle of a neighbouring point.
    static CURSOR_LAYER_IDS = ['lyr-transactions-hit', 'lyr-transactions-fill', 'lyr-transactions-line'];
    static HIGHLIGHT_SOURCE_ID = 'src-transaction-highlight';
    static HIGHLIGHT_LAYER_IDS = ['lyr-transaction-highlight-ring', 'lyr-transaction-highlight-fill', 'lyr-transaction-highlight-casing'];
    // Drawing order of the selection: fill, white casing, navy ring or outline.
    static HIGHLIGHT_RENDER_ORDER = ['lyr-transaction-highlight-fill', 'lyr-transaction-highlight-casing', 'lyr-transaction-highlight-ring'];
    static RENDER_ORDER_LAYER_IDS = TransactionsLayer.BASE_LAYER_IDS.concat(TransactionsLayer.HIGHLIGHT_RENDER_ORDER);
    // Dots: the department navy with a white ring that separates neighbours and
    // stands out from roads. Outlines: a translucent fill keeps the basemap
    // readable, a solid navy line draws the parcel or building boundary.
    static BASE_STYLE = {
        point: {
            'circle-radius': 7,
            'circle-color': TRANSACTION_COLOR,
            'circle-opacity': 0.9,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1.5,
            'circle-stroke-opacity': 0.9
        },
        polygonFill: { 'fill-color': TRANSACTION_COLOR, 'fill-opacity': 0.35 },
        polygonLine: { 'line-color': TRANSACTION_COLOR, 'line-width': 1.8 }
    };
    // The selected transaction: a wider ring, or a thick outline over a denser
    // fill. A white casing under the outline keeps it visible on stacked
    // outlines (several sales of one parcel or building), which are dark navy.
    static HIGHLIGHT_STYLE = {
        point: {
            'circle-radius': 14,
            'circle-color': transactionColor(0.22),
            'circle-stroke-color': TRANSACTION_COLOR,
            'circle-stroke-width': 3
        },
        polygonFill: { 'fill-color': transactionColor(0.42), 'fill-opacity': 1 },
        polygonCasing: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.95 },
        polygonLine: { 'line-color': TRANSACTION_COLOR, 'line-width': 3.5 }
    };
    static VISIBLE_DOT_RADIUS = TransactionsLayer.BASE_STYLE.point['circle-radius'] + TransactionsLayer.BASE_STYLE.point['circle-stroke-width'];
    static AGGREGATE_COLOR = transactionColor(0.92);

    constructor(config = {}) {
        super(config);
        this.interactionsBound = false;
        this.lastFilterFingerprint = null;
    }

    getSourceId() { return TransactionsLayer.SOURCE_ID; }
    getLayerIds() { return TransactionsLayer.BASE_LAYER_IDS; }
    getRenderOrderLayerIds() { return TransactionsLayer.RENDER_ORDER_LAYER_IDS; }

    clear() {
        this.abortPending();
        this.resetAccumulator();
        this.lastFilterFingerprint = null;
        const map = this.getMap();
        const layer = this.config.getLayer ? this.config.getLayer() : null;
        if (layer && typeof layer.remove === 'function') layer.remove();
        if (map) {
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
            this.clearHighlight();
        }
        if (this.config.setLayer) this.config.setLayer(null);
        if (this.config.onCleared) this.config.onCleared(this);
    }

    beforeRequest() {
        if (this.config.onLoadingChange) this.config.onLoadingChange(true);
    }

    afterRequest() {
        if (this.config.onLoadingChange) this.config.onLoadingChange(false);
    }

    buildUrl() {
        const fingerprint = this.getFilterFingerprint();
        if (this.lastFilterFingerprint !== null && fingerprint !== this.lastFilterFingerprint) {
            this.resetAccumulator();
        }
        this.lastFilterFingerprint = fingerprint;
        const params = new URLSearchParams(this.config.getGeoQuery ? this.config.getGeoQuery() : '');
        return 'api/geojson?' + params.toString();
    }

    ensureInteractionHandlers() {
        const map = this.getMap();
        if (!map) return;
        if (this.interactionsBound) return;
        registerCursorHandlers(map, TransactionsLayer.CURSOR_LAYER_IDS);
        this.interactionsBound = true;
    }

    createClusterGroup(features) {
        const map = this.getMap();
        if (!map) return null;
        const aggGroup = new MarkerGroup();
        const formatAggCount = this.config.formatAggCount || ((value) => String(value));
        const edgePadding = this.config.getClusterViewportPadding ? this.config.getClusterViewportPadding() : 24;
        const canvas = map.getCanvas();
        const markers = groupTransactionMarkers(features, coordinates => map.project(coordinates), canvas.clientWidth, canvas.clientHeight);
        markers.forEach((feature) => {
            if (!feature.geometry || feature.geometry.type !== 'Point') return;
            const props = feature.properties || {};
            const count = Number(props.count || 1);
            const size = count < 10 ? 10 : count < 100 ? 13 : count < 1000 ? 16 : 19;
            const diameter = size * 2;
            const marker = createHtmlMarker(
                feature.geometry.coordinates,
                '<div class="nm-agg-dot nm-agg-dot--transactions" style="width:' + diameter + 'px;height:' + diameter + 'px;background:' + TransactionsLayer.AGGREGATE_COLOR + '">'
                    + '<span class="nm-agg-count">' + formatAggCount(count) + '</span></div>',
                'nm-agg-wrap'
            );
            marker.getElement().addEventListener('click', (event) => {
                event.stopPropagation();
                drillDownTransactionCluster(map, feature, { padding: edgePadding });
            });
            aggGroup.add(marker);
        });
        aggGroup.addTo(map);
        return aggGroup;
    }

    renderAggregatedData(data) { return this.createClusterGroup((data && data.features) || []); }

    getFeatureKey(feature) {
        if (!feature || !feature.properties) return null;
        return feature.properties.feature_id || feature.properties.tran_lokalny_id_iip || null;
    }

    getFilterFingerprint() {
        return this.fingerprintFromQuery(this.config.getGeoQuery ? this.config.getGeoQuery() : '');
    }

    _addBaseLayers(map, sourceId) {
        map.addLayer({ id: TransactionsLayer.BASE_LAYER_IDS[0], type: 'circle', source: sourceId, filter: ['==', '$type', 'Point'], paint: TransactionsLayer.BASE_STYLE.point });
        map.addLayer({ id: TransactionsLayer.BASE_LAYER_IDS[1], type: 'fill', source: sourceId, filter: ['!=', '$type', 'Point'], paint: TransactionsLayer.BASE_STYLE.polygonFill });
        map.addLayer({ id: TransactionsLayer.BASE_LAYER_IDS[2], type: 'line', source: sourceId, filter: ['!=', '$type', 'Point'], paint: TransactionsLayer.BASE_STYLE.polygonLine });
        map.addLayer(pointHitLayer(TransactionsLayer.HIT_LAYER_ID, sourceId, pointHitRadius(isCoarsePointer(typeof window !== 'undefined' ? window : null)), TransactionsLayer.VISIBLE_DOT_RADIUS));
        this.ensureInteractionHandlers();
    }

    renderIndividualData(data) {
        const map = this.getMap();
        if (!map) return null;
        addOrUpdateGeoJsonSource(map, this.getSourceId(), data);
        this._addBaseLayers(map, this.getSourceId());
        return this.getSourceId();
    }

    renderData(data) {
        const map = this.getMap();
        if (!map) return null;
        const isAggregated = data && data.aggregated === true;
        if (!isAggregated && data && Array.isArray(data.features)) {
            data.features.sort((a, b) => {
                const left = (a.properties && (a.properties.dok_date || a.properties.tran_wersja_id)) || '';
                const right = (b.properties && (b.properties.dok_date || b.properties.tran_wersja_id)) || '';
                return left < right ? -1 : left > right ? 1 : 0;
            });
        }

        if (isAggregated || !this.accumulator) {
            this.resetAccumulator();
            const currentLayer = this.config.getLayer ? this.config.getLayer() : null;
            if (currentLayer && typeof currentLayer.remove === 'function') currentLayer.remove();
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
            const layer = isAggregated ? this.renderAggregatedData(data) : this.renderIndividualData(data);
            this._finishRender(data, layer);
            return layer;
        }

        const previous = this.config.getLayer ? this.config.getLayer() : null;
        if (previous && typeof previous.remove === 'function') previous.remove();
        return this.renderSourceData(data, this._addBaseLayers.bind(this));
    }

    clearHighlight() {
        const map = this.getMap();
        if (map) removeSourceAndLayers(map, TransactionsLayer.HIGHLIGHT_SOURCE_ID, TransactionsLayer.HIGHLIGHT_LAYER_IDS);
    }

    renderHighlightData(data) {
        const map = this.getMap();
        if (!map || !data || !Array.isArray(data.features) || !data.features.length) {
            this.clearHighlight();
            return { found: false, center: null };
        }
        this.clearHighlight();
        addOrUpdateGeoJsonSource(map, TransactionsLayer.HIGHLIGHT_SOURCE_ID, data);
        if (data.features.every(feature => feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint')) {
            map.addLayer({ id: TransactionsLayer.HIGHLIGHT_LAYER_IDS[0], type: 'circle', source: TransactionsLayer.HIGHLIGHT_SOURCE_ID, paint: TransactionsLayer.HIGHLIGHT_STYLE.point });
        } else {
            this._addHighlightOutline(map);
        }
        return { found: true, center: getFeatureCenter(data.features[0]) };
    }

    highlightFeatureById(transactionId, data) {
        if (!transactionId || !data || !Array.isArray(data.features)) return { found: false, center: null };
        const matchedFeature = data.features.find((feature) => feature.properties && feature.properties.aggregated !== true && feature.properties.tran_lokalny_id_iip === transactionId);
        this.clearHighlight();
        if (!matchedFeature || !matchedFeature.geometry) return { found: false, center: null };
        const map = this.getMap();
        if (!map) return { found: false, center: null };
        addOrUpdateGeoJsonSource(map, TransactionsLayer.HIGHLIGHT_SOURCE_ID, { type: 'FeatureCollection', features: [matchedFeature] });
        if (matchedFeature.geometry.type === 'Point' || matchedFeature.geometry.type === 'MultiPoint') {
            map.addLayer({ id: TransactionsLayer.HIGHLIGHT_LAYER_IDS[0], type: 'circle', source: TransactionsLayer.HIGHLIGHT_SOURCE_ID, paint: TransactionsLayer.HIGHLIGHT_STYLE.point });
        } else {
            this._addHighlightOutline(map);
        }
        return { found: true, center: getFeatureCenter(matchedFeature) };
    }

    _addHighlightOutline(map) {
        const source = TransactionsLayer.HIGHLIGHT_SOURCE_ID;
        const [ringId, fillId, casingId] = TransactionsLayer.HIGHLIGHT_LAYER_IDS;
        map.addLayer({ id: fillId, type: 'fill', source, filter: ['!=', '$type', 'Point'], paint: TransactionsLayer.HIGHLIGHT_STYLE.polygonFill });
        map.addLayer({ id: casingId, type: 'line', source, filter: ['!=', '$type', 'Point'], paint: TransactionsLayer.HIGHLIGHT_STYLE.polygonCasing });
        map.addLayer({ id: ringId, type: 'line', source, filter: ['!=', '$type', 'Point'], paint: TransactionsLayer.HIGHLIGHT_STYLE.polygonLine });
    }

    handleError(error) {
        if (this.config.onFetchError) {
            this.config.onFetchError(error, this);
            return;
        }
        super.handleError(error);
    }
}
